/**
 * Data Source Formatters and Transformers
 */
import { logger } from '@/utils/logger'
import { arrayToCsv } from '@/utils/csv'
import { useI18n } from '@/composables/useI18n'
import type {
  DashboardAPIResponse,
  DashboardReport,
  ClinicalDomainAPIResponse,
  PrevalenceData,
  TreemapNode,
  PrevalenceTableRow,
  HistogramChartData,
  PieChartData,
  LineChartData,
  ReportType,
} from '@/models/datasource.types'

export { formatNumber } from '@/utils/format'

const DAYS_PER_YEAR = 365.25

/**
 * Observation x-values arrive from WebAPI as length-of-observation in DAYS.
 * Matching the original Atlas behaviour, display them in years when the span is
 * large (> 1000 days ≈ 2.7y); otherwise keep days. `days` must be sorted ascending.
 */
function lengthOfObservationAxis(days: number[]): { values: number[]; label: string } {
  const { tv } = useI18n()
  const span = days.length ? (days[days.length - 1] ?? 0) - (days[0] ?? 0) : 0
  if (span > 1000) {
    return { values: days.map(d => Math.round((d / DAYS_PER_YEAR) * 100) / 100), label: tv('dataSources.observationPeriodReport.years') }
  }
  return { values: days, label: tv('dataSources.observationPeriodReport.days') }
}

/**
 * Transform Dashboard API response to internal format
 */
export function transformDashboardReport(raw: DashboardAPIResponse): DashboardReport {
  const { tv } = useI18n()
  const summary = {
    sourceName: raw.summary.find(s => s.attributeName === 'Source name')?.attributeValue || '',
    personCount: parseInt(
      raw.summary.find(s => s.attributeName === 'Number of persons')?.attributeValue || '0'
    ),
  }

  const genderDistribution: PieChartData[] = raw.gender.map(g => ({
    name: g.conceptName,
    value: g.countValue,
  }))

  const ageDistribution: HistogramChartData = {
    intervalSize: 1,
    offset: 0,
    bins: raw.ageAtFirstObservation.map(a => ({
      intervalIndex: a.intervalIndex,
      countValue: a.countValue,
    })),
    unit: tv('columns.personCount'),
    seriesName: tv('columns.personCount'),
    xAxisLabel: tv('columns.age'),
  }

  const cumulativeSorted = (raw.cumulativeObservation || [])
    .slice()
    .sort((a, b) => (a.xLengthOfObservation ?? 0) - (b.xLengthOfObservation ?? 0))
  const cumulativeAxis = lengthOfObservationAxis(cumulativeSorted.map(c => c.xLengthOfObservation ?? 0))
  const cumulativeObservation: LineChartData = {
    categories: cumulativeSorted.map(c => c.xLengthOfObservation?.toString() || ''),
    xValues: cumulativeAxis.values,
    series: [
      {
        name: tv('dataSources.dashboardReport.cumulativeObservation'),
        data: cumulativeSorted.map(c => c.yPercentPersons || 0),
      },
    ],
    xAxisLabel: cumulativeAxis.label,
    yAxisLabel: tv('dataSources.charts.percentOfPersons'),
  }

  const observationByMonth: LineChartData = {
    // Order by `monthYear` before splitting into parallel arrays
    ...(() => {
      const sorted = (raw.observedByMonth || []).slice().sort((a, b) => (a.monthYear ?? 0) - (b.monthYear ?? 0))
      return {
        categories: sorted.map(o => o.monthYear?.toString() || ''),
        monthCodes: sorted.map(o => o.monthYear ?? 0),
        xAxisType: 'time' as const,
        series: [
          {
            name: tv('dataSources.charts.observationCount'),
            data: sorted.map(o => o.countValue || 0),
          },
        ],
      }
    })(),
    xAxisLabel: tv('dataSources.charts.month'),
    yAxisLabel: tv('columns.count'),
  }

  return {
    summary,
    genderDistribution,
    ageDistribution,
    cumulativeObservation,
    observationByMonth,
  }
}

/**
 * Transform Clinical Domain API response to prevalence data
 */
export function transformClinicalDomainReport(
  raw: ClinicalDomainAPIResponse[],
  reportType: ReportType
): PrevalenceData {
  const isEra = isEraReport(reportType)
  const AGGREGATION_THRESHOLD = 10000

  // For very large datasets, keep only the top entries by prevalence for
  // display/performance. We used to fold the remainder into a synthetic
  // "Other" row (conceptId -1), but percentPersons is not a mutually
  // exclusive share of the population (a person can hold many distinct
  // concepts), so summing it across thousands of rows produced nonsensical
  // prevalence values well over 100%. Each concept is now shown
  // individually; the caller's own dataset-size hint (and search/export)
  // covers discoverability of the truncated tail.
  let processedRaw = raw
  if (raw.length > AGGREGATION_THRESHOLD) {
    logger.info('Transformer', `Large dataset detected (${raw.length} entries), truncating to top 1000`)
    const sorted = [...raw].sort((a, b) => b.percentPersons - a.percentPersons)
    processedRaw = sorted.slice(0, 1000)
  }

  const tableRows: PrevalenceTableRow[] = processedRaw.map(item => ({
    conceptId: item.conceptId,
    conceptName: extractConceptDisplayName(item.conceptPath),
    personCount: item.numPersons,
    prevalence: item.percentPersons * 100, // percentPersons arrives as a 0..1 fraction
    metric: isEra ? item.lengthOfEra || 0 : item.recordsPerPerson || 0,
  }))

  // Atlas 2.15 treemap encoding (see js/components/reports/classes/Treemap.js
  // and per-domain aggProperty in js/pages/data-sources/components/reports/*):
  //   - rectangle area  → numPersons (size)
  //   - rectangle colour → recordsPerPerson for non-era reports,
  //                         lengthOfEra for era reports.
  // We carry colour magnitude on TreemapNode.colorValue so the
  // gradient shader (paintTreemapNodesByValue) can scale it
  // independently of `value`. Drop the legacy colorAlpha — that
  // was a (different) prevalence-based dimming hack.
  const treemapNodes: TreemapNode[] = processedRaw.map(item => {
    const colorValueRaw = isEra ? item.lengthOfEra : item.recordsPerPerson
    const colorValue =
      typeof colorValueRaw === 'number' && Number.isFinite(colorValueRaw)
        ? colorValueRaw
        : undefined
    return {
      name: extractConceptDisplayName(item.conceptPath),
      value: item.numPersons,
      colorValue,
      conceptId: item.conceptId,
      conceptPath: item.conceptPath,
    }
  })

  return {
    treemapNodes,
    tableRows,
    totalCount: raw.length, // Original count before aggregation
  }
}

/**
 * Extract display name from concept path
 * Concept paths are in format "Level1||Level2||Name"
 * Returns only the last element for display
 */
function extractConceptDisplayName(conceptPath: string): string {
  if (!conceptPath) return ''
  const parts = conceptPath.split('||')
  return parts[parts.length - 1]?.trim() || ''
}

/**
 * Determine if report type is an Era report
 */
export function isEraReport(reportType: ReportType): boolean {
  return reportType === 'conditionEra' || reportType === 'drugEra'
}

/**
 * Get metric column label for report type
 */
export function getMetricLabel(reportType: ReportType): string {
  const { tv } = useI18n()
  return isEraReport(reportType)
    ? tv('components.drilldownDetails.lengthOfEra')
    : tv('dataSources.datadensityReport.recordsPerPerson')
}

/**
 * Export table data to CSV format
 */
export function exportTableToCSV(rows: PrevalenceTableRow[], metricLabel: string): string {
  return arrayToCsv(rows, [
    { key: 'conceptId', label: 'Concept ID' },
    { key: 'conceptName', label: 'Name' },
    { key: 'personCount', label: 'Person Count' },
    { key: row => row.prevalence.toFixed(2), label: 'Prevalence (%)' },
    { key: row => row.metric.toFixed(2), label: metricLabel },
  ])
}

/**
 * Format percentage with fixed decimal places
 */
export function formatPercentage(num: number, decimals = 2): string {
  return `${num.toFixed(decimals)}%`
}

interface DataDensityRawItem {
  xCalendarMonth?: number
  seriesName?: string
  yRecordCount?: number
  category?: string
  minValue?: number
  p10Value?: number
  p25Value?: number
  medianValue?: number
  p75Value?: number
  p90Value?: number
  maxValue?: number
}

interface DataDensityRaw {
  totalRecords?: DataDensityRawItem[]
  recordsPerPerson?: DataDensityRawItem[]
  conceptsPerPerson?: DataDensityRawItem[]
}

/**
 * Transform Data Density API response to internal format
 */
export function transformDataDensityReport(
  raw: DataDensityRaw
): import('@/models/datasource.types').DataDensityReport {
  const { tv } = useI18n()
  // Build union of months across all series for totalRecords so categories include every month
  const totalRaw = raw.totalRecords || []
    const totalMonthsSet = new Set<number>()
    const totalSeriesNames = new Set<string>()
    const totalLookup = new Map<string, number>()
    totalRaw.forEach(item => {
      const month = item.xCalendarMonth
      if (month == null) return
      totalMonthsSet.add(month)
      const seriesName = item.seriesName || tv('dataSources.charts.total')
      totalSeriesNames.add(seriesName)
      totalLookup.set(`${seriesName}|${month}`, item.yRecordCount ?? 0)
    })
    const totalMonths = Array.from(totalMonthsSet).sort((a, b) => a - b)
    const totalCategories = totalMonths.map(m => m.toString())

  // totalLookup already populated above in the single iteration

  const totalRecords: import('@/models/datasource.types').MultiLineChartData = {
    categories: totalCategories,
    xAxisType: 'time',
    monthCodes: totalMonths,
    series: Array.from(totalSeriesNames).map(name => ({
      name,
      data: totalMonths.map(m => totalLookup.get(`${name}|${m}`) ?? 0),
    })),
  }

  // Same for recordsPerPerson: union months across its series and align each series
  const recRaw = raw.recordsPerPerson || []
  const recMonthsSet = new Set<number>()
  const recSeriesNames = new Set<string>()
  const recLookup = new Map<string, number>()
  recRaw.forEach(item => {
      const month = item.xCalendarMonth
      if (month == null) return
      recMonthsSet.add(month)
      const seriesName = item.seriesName || tv('dataSources.charts.records')
      recSeriesNames.add(seriesName)
      recLookup.set(`${seriesName}|${month}`, item.yRecordCount ?? 0)
    })
    const recMonths = Array.from(recMonthsSet).sort((a, b) => a - b)
    const recCategories = recMonths.map(m => m.toString())

  // recLookup already populated above in the single iteration

  const recordsPerPerson: import('@/models/datasource.types').MultiLineChartData = {
    categories: recCategories,
    xAxisType: 'time',
    monthCodes: recMonths,
    series: Array.from(recSeriesNames).map(name => ({
      name,
      data: recMonths.map(m => recLookup.get(`${name}|${m}`) ?? 0),
    })),
  }

  const conceptsPerPerson = (raw.conceptsPerPerson || []).map(item => ({
    category: item.category || '',
    min: item.minValue ?? 0,
    p10: item.p10Value ?? 0,
    p25: item.p25Value ?? 0,
    median: item.medianValue ?? 0,
    p75: item.p75Value ?? 0,
    p90: item.p90Value ?? 0,
    max: item.maxValue ?? 0,
  }))

  return {
    totalRecords,
    recordsPerPerson,
    conceptsPerPerson,
  }
}

interface PersonRawYearOfBirth {
  intervalIndex?: number
  percentValue?: number
  countValue?: number
}

interface PersonRawYearOfBirthStats {
  minValue?: number
  maxValue?: number
  intervalSize?: number
}

interface PersonRawDistribution {
  conceptName?: string
  name?: string
  countValue?: number
  count?: number
}

interface PersonRaw {
  yearOfBirth?: PersonRawYearOfBirth[]
  yearOfBirthStats?: PersonRawYearOfBirthStats[]
  gender?: PersonRawDistribution[]
  race?: PersonRawDistribution[]
  ethnicity?: PersonRawDistribution[]
}

/**
 * Transform Person API response to internal format.
 * Mirrors Atlas 2.x logic:
 *   offset       = yearOfBirthStats[0].minValue   (e.g. 1910)
 *   intervalSize = yearOfBirthStats[0].intervalSize (e.g. 1)
 *   bins         = yearOfBirth[].intervalIndex (0-based from offset)
 *   → actual year = offset + intervalIndex * intervalSize
 */
export function transformPersonReport(
  raw: PersonRaw
): import('@/models/datasource.types').PersonReport {
  const { tv } = useI18n()
  // Year of birth distribution
  const yearOfBirth: import('@/models/datasource.types').HistogramChartData = (() => {
    const yearData = raw.yearOfBirth || []
    if (yearData.length === 0) {
      return {
        intervalSize: 1,
        offset: 0,
        bins: [],
        unit: tv('columns.personCount'),
        seriesName: tv('columns.personCount'),
        xAxisLabel: tv('dataSources.personReport.yearOfBirth'),
      }
    }

    const stats = raw.yearOfBirthStats?.[0]
    const offset = stats?.minValue ?? 0
    const intervalSize = stats?.intervalSize ?? 1

    const bins = yearData
      .map(y => ({
        intervalIndex: y.intervalIndex ?? 0,
        countValue: y.countValue ?? 0,
      }))
      .sort((a, b) => a.intervalIndex - b.intervalIndex)

    return {
      intervalSize,
      offset,
      bins,
      unit: tv('columns.personCount'),
      seriesName: tv('columns.personCount'),
      xAxisLabel: tv('dataSources.personReport.yearOfBirth'),
    }
  })()

  // Gender distribution
  const gender: import('@/models/datasource.types').PieChartData[] =
    raw.gender?.map(g => ({
      name: g.conceptName || g.name || tv('dataSources.charts.unknown'),
      value: g.countValue || g.count || 0,
    })) || []

  // Race distribution
  const race: import('@/models/datasource.types').PieChartData[] =
    raw.race?.map(r => ({
      name: r.conceptName || r.name || tv('dataSources.charts.unknown'),
      value: r.countValue || r.count || 0,
    })) || []

  // Ethnicity distribution
  const ethnicity: import('@/models/datasource.types').PieChartData[] =
    raw.ethnicity?.map(e => ({
      name: e.conceptName || e.name || tv('dataSources.charts.unknown'),
      value: e.countValue || e.count || 0,
    })) || []

  return {
    yearOfBirth,
    gender,
    race,
    ethnicity,
  }
}

interface RawBoxPlotItem {
  category?: string
  minValue?: number
  p10Value?: number
  p25Value?: number
  medianValue?: number
  p75Value?: number
  p90Value?: number
  maxValue?: number
}

function mapBoxPlotArray(
  raw: RawBoxPlotItem[] | undefined
): import('@/models/report.types').BoxPlotData[] | undefined {
  if (!raw || raw.length === 0) return undefined
  return raw.map(item => ({
    category: item.category || '',
    min: item.minValue ?? 0,
    p10: item.p10Value ?? 0,
    p25: item.p25Value ?? 0,
    median: item.medianValue ?? 0,
    p75: item.p75Value ?? 0,
    p90: item.p90Value ?? 0,
    max: item.maxValue ?? 0,
  }))
}

interface ObservationPeriodRawHistItem {
  intervalIndex?: number
  percentValue?: number
  countValue?: number
}

interface ObservationPeriodRawCumulativeItem {
  xLengthOfObservation?: number
  yPercentPersons?: number
}

interface ObservationPeriodRawMonthItem {
  monthYear?: number
  countValue?: number
}

interface ObservationPeriodsPerPersonItem {
  conceptName?: string
  countValue?: number
}

interface ObservationPeriodRaw {
  ageAtFirst?: ObservationPeriodRawHistItem[]
  observationLength?: ObservationPeriodRawHistItem[]
  cumulativeObservation?: ObservationPeriodRawCumulativeItem[]
  observedByMonth?: ObservationPeriodRawMonthItem[]
  ageByGender?: RawBoxPlotItem[]
  durationByGender?: RawBoxPlotItem[]
  durationByAgeDecile?: RawBoxPlotItem[]
  personsWithContinuousObservationsByYear?: ObservationPeriodRawHistItem[]
  observationPeriodsPerPerson?: ObservationPeriodsPerPersonItem[]
  observationLengthStats?: Array<{ minValue?: number; maxValue?: number; intervalSize?: number }>
}

/**
 * Transform Observation Period Report
 * Specialized transformer for observation period data
 */
export function transformObservationPeriodReport(
  raw: ObservationPeriodRaw
): import('@/models/datasource.types').ObservationPeriodReport {
  const { tv } = useI18n()
  // ageAtFirst: offset=0, intervalSize=1 — intervalIndex IS the age (matches Atlas 2.x parseHistogramData)
  const ageAtFirst: import('@/models/datasource.types').HistogramChartData | undefined =
    raw.ageAtFirst
      ? {
          intervalSize: 1,
          offset: 0,
          bins: raw.ageAtFirst
            .map(i => ({
              intervalIndex: i.intervalIndex ?? 0,
              countValue: i.countValue ?? 0,
            }))
            .sort((a, b) => a.intervalIndex - b.intervalIndex),
          unit: tv('columns.personCount'),
          seriesName: tv('columns.personCount'),
          xAxisLabel: tv('columns.age'),
        }
      : undefined

  // observationLength: offset=0, intervalSize from observationLengthStats (e.g. 30 days per bin)
  // x-axis value = intervalIndex * intervalSize (days)
  // Mirrors Atlas 2.x: parseObservationLength sets OFFSET=0, INTERVAL_SIZE=stats.intervalSize
  const observationLength: import('@/models/datasource.types').HistogramChartData | undefined =
    raw.observationLength
      ? (() => {
          const intervalSizeDays = raw.observationLengthStats?.[0]?.intervalSize ?? 1
          const bins = raw.observationLength
            .map(i => ({
              intervalIndex: i.intervalIndex ?? 0,
              countValue: i.countValue ?? 0,
            }))
            .sort((a, b) => a.intervalIndex - b.intervalIndex)
          // x-position of a bin = intervalIndex * intervalSize (days). Show years
          // when the span is large, scaling the interval so bins land on year ticks.
          const maxDays = bins.length ? (bins[bins.length - 1]?.intervalIndex ?? 0) * intervalSizeDays : 0
          const inYears = maxDays > 1000
          return {
            intervalSize: inYears ? intervalSizeDays / DAYS_PER_YEAR : intervalSizeDays,
            offset: 0,
            bins,
            unit: tv('columns.personCount'),
            seriesName: tv('columns.personCount'),
            xAxisLabel: inYears
              ? tv('dataSources.observationPeriodReport.years')
              : tv('dataSources.observationPeriodReport.days'),
          }
        })()
      : undefined

  const cumulativeObservation: import('@/models/datasource.types').MultiLineChartData | undefined =
    raw.cumulativeObservation
      ? (() => {
          const sorted = (raw.cumulativeObservation || []).slice().sort((a, b) => (a.xLengthOfObservation ?? 0) - (b.xLengthOfObservation ?? 0))
          const axis = lengthOfObservationAxis(sorted.map(i => i.xLengthOfObservation ?? 0))
          return {
            categories: sorted.map(i => i.xLengthOfObservation?.toString() || ''),
            xAxisType: 'value' as const,
            xValues: axis.values,
            xAxisLabel: axis.label,
            yAxisLabel: tv('dataSources.charts.percentOfPersons'),
            yAxisFormat: 'percent' as const,
            series: [
              {
                name: tv('dataSources.charts.cumulativePercent'),
                data: sorted.map(i => i.yPercentPersons || 0),
              },
            ],
          }
        })()
      : undefined

  const observedByMonth: import('@/models/datasource.types').MultiLineChartData | undefined =
    raw.observedByMonth
      ? (() => {
          const sorted = (raw.observedByMonth || []).slice().sort((a, b) => (a.monthYear ?? 0) - (b.monthYear ?? 0))
          return {
            categories: sorted.map(i => i.monthYear?.toString() || ''),
            xAxisType: 'time' as const,
            monthCodes: sorted.map(i => i.monthYear ?? 0),
            series: [
              {
                name: tv('dataSources.charts.persons'),
                data: sorted.map(i => i.countValue || 0),
              },
            ],
          }
        })()
      : undefined

  const personsWithContinuousObsByYear = raw.personsWithContinuousObservationsByYear
    ? {
        categories: raw.personsWithContinuousObservationsByYear.map(
          i => i.intervalIndex?.toString() || ''
        ),
        values: raw.personsWithContinuousObservationsByYear.map(i => i.countValue || 0),
      }
    : undefined

  const observationPeriodsPerPerson:
    | import('@/models/datasource.types').PieChartData[]
    | undefined =
    raw.observationPeriodsPerPerson && raw.observationPeriodsPerPerson.length > 0
      ? raw.observationPeriodsPerPerson.map(i => ({
          name: i.conceptName || tv('dataSources.charts.unknown'),
          value: i.countValue || 0,
        }))
      : undefined

  return {
    ageAtFirst,
    observationLength,
    cumulativeObservation,
    observedByMonth,
    ageByGender: mapBoxPlotArray(raw.ageByGender),
    durationByGender: mapBoxPlotArray(raw.durationByGender),
    durationByAgeDecile: mapBoxPlotArray(raw.durationByAgeDecile),
    personsWithContinuousObsByYear,
    observationPeriodsPerPerson,
  }
}

interface DeathRawDeathByType {
  conceptName?: string
  countValue?: number
}

interface DeathRawPrevalenceByMonth {
  xCalendarMonth?: number
  yPrevalence1000Pp?: number
}

interface DeathRawPrevalenceByGenderAgeYear {
  xCalendarYear?: number
  trellisName?: string
  seriesName?: string
  yPrevalence1000Pp?: number
}

interface DeathRawAgeAtDeath extends RawBoxPlotItem {
  conceptId?: number
}

interface DeathRaw {
  ageAtDeath?: DeathRawAgeAtDeath[]
  deathByType?: DeathRawDeathByType[]
  prevalenceByMonth?: DeathRawPrevalenceByMonth[]
  prevalenceByGenderAgeYear?: DeathRawPrevalenceByGenderAgeYear[]
}

export function transformDeathReport(
  raw: DeathRaw
): import('@/models/datasource.types').DeathReport {
  const { tv } = useI18n()
  const ageAtDeath = mapBoxPlotArray(raw.ageAtDeath) || []

  const deathByType: import('@/models/datasource.types').PieChartData[] =
    raw.deathByType?.map(item => ({
      name: item.conceptName || tv('dataSources.charts.unknown'),
      value: item.countValue || 0,
    })) || []

  const prevalenceByMonth: import('@/models/datasource.types').MultiLineChartData | undefined =
    raw.prevalenceByMonth
      ? (() => {
          const sorted = (raw.prevalenceByMonth || []).slice().sort((a, b) => (a.xCalendarMonth ?? 0) - (b.xCalendarMonth ?? 0))
          return {
            categories: sorted.map(i => i.xCalendarMonth?.toString() || ''),
            xAxisType: 'time' as const,
            monthCodes: sorted.map(i => i.xCalendarMonth ?? 0),
            series: [
              {
                name: tv('dataSources.charts.prevalencePer1000'),
                data: sorted.map(i => i.yPrevalence1000Pp || 0),
              },
            ],
          }
        })()
      : undefined

  let prevalenceByGenderAgeYear: import('@/models/report.types').TrellisChartData | undefined
  if (raw.prevalenceByGenderAgeYear && raw.prevalenceByGenderAgeYear.length > 0) {
    const groupedData = new Map<string, Map<string, { x: number; y: number }[]>>()
    const categories = new Set<string>()

    raw.prevalenceByGenderAgeYear.forEach(item => {
      const trellisName = item.trellisName || tv('dataSources.charts.unknown')
      const seriesName = item.seriesName || tv('dataSources.charts.total')
      const year = item.xCalendarYear || 0
      const prevalence = item.yPrevalence1000Pp || 0

      categories.add(trellisName)
      if (!groupedData.has(trellisName)) groupedData.set(trellisName, new Map())
      const trellisGroup = groupedData.get(trellisName)!
      if (!trellisGroup.has(seriesName)) trellisGroup.set(seriesName, [])
      trellisGroup.get(seriesName)!.push({ x: year, y: prevalence })
    })

    const series: import('@/models/report.types').TrellisSeriesData[] = []
    groupedData.forEach((seriesMap, category) => {
      seriesMap.forEach((data, name) => {
        series.push({ name, category, data: data.sort((a, b) => a.x - b.x) })
      })
    })

    prevalenceByGenderAgeYear = { series, categories: Array.from(categories) }
  }

  return { ageAtDeath, deathByType, prevalenceByMonth, prevalenceByGenderAgeYear }
}
